import { Fragment, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { DataTable } from "@/components/ui/DataTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { Pagination } from "@/components/ui/Pagination";
import { formatDate } from "@bookboost/shared/format";

interface AuditAction {
  id: string;
  actor_id: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  detail: unknown;
  created_at: string | null;
  actor: { email: string | null; full_name: string | null } | null;
}

const auditSearch = z.object({
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute("/admin/audit")({
  validateSearch: auditSearch,
  component: AuditPage,
});

function AuditPage() {
  const { page } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "audit", { page }],
    queryFn: () =>
      api.get<{ actions: AuditAction[]; total: number; page: number; pageSize: number }>("/api/admin/audit", {
        searchParams: { page },
      }),
  });

  return (
    <>
      <h1>Audit log</h1>

      <Panel padding="tight">
        {isLoading ? (
          <p className="lede">Loading…</p>
        ) : error ? (
          <p className="err">{error instanceof Error ? error.message : "Failed to load audit log."}</p>
        ) : !data || data.actions.length === 0 ? (
          <EmptyState
            icon="generic"
            title="No admin actions recorded yet"
            body="Admin mutations (approvals, reversals, recalcs, structure edits, …) are appended here as they happen. The log is read-only by design."
          />
        ) : (
          <DataTable>
            <table className="table-cards">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th aria-label="Detail" />
                </tr>
              </thead>
              <tbody>
                {data.actions.map((a) => {
                  const isOpen = expanded === a.id;
                  const hasDetail = a.detail != null;
                  return (
                    <Fragment key={a.id}>
                      <tr>
                        <td data-label="Time" className="muted">{formatDate(a.created_at)}</td>
                        <td data-label="Actor">
                          {a.actor ? (
                            <span title={a.actor.email ?? undefined}>
                              {a.actor.full_name ?? a.actor.email ?? "—"}
                            </span>
                          ) : (
                            <span className="muted system-actor">system</span>
                          )}
                        </td>
                        <td data-label="Action">
                          <span className="action-chip"><code>{a.action}</code></span>
                        </td>
                        <td data-label="Target" className="muted target-cell">
                          {a.target_type ? (
                            <>
                              {a.target_type}{" "}
                              {a.target_id && (
                                <code title={a.target_id}>{truncateId(a.target_id)}</code>
                              )}
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td data-label="" className="detail-toggle-cell">
                          {hasDetail && (
                            <button
                              type="button"
                              className="detail-toggle"
                              aria-expanded={isOpen}
                              onClick={() => setExpanded(isOpen ? null : a.id)}
                            >
                              {isOpen ? "Hide" : "Detail"}
                            </button>
                          )}
                        </td>
                      </tr>
                      {isOpen && hasDetail && (
                        <tr className="detail-row">
                          <td colSpan={5}>
                            <div className="detail-body">
                              <pre className="detail-json">{JSON.stringify(a.detail, null, 2)}</pre>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </DataTable>
        )}
        {data && data.total > 0 && (
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={(p) => navigate({ search: { page: p } })}
          />
        )}
      </Panel>

      <style>{auditPageStyles}</style>
    </>
  );
}

function truncateId(id: string): string {
  return id.length > 14 ? `${id.slice(0, 12)}…` : id;
}

const auditPageStyles = `
  .system-actor { font-style: italic; }
  .action-chip {
    display: inline-block; padding: 2px 8px; border-radius: 999px;
    background: color-mix(in srgb, var(--text-strong) 6%, transparent);
    border: 1px solid var(--border);
    font-size: 12px; white-space: nowrap;
  }
  .action-chip code { background: none; padding: 0; }
  .target-cell { font-size: 12.5px; white-space: nowrap; }
  .detail-toggle-cell { text-align: right; white-space: nowrap; }
  .detail-toggle {
    font-size: 12px; padding: 4px 10px; border-radius: 6px; cursor: pointer;
    border: 1px solid var(--border); background: var(--surface); color: var(--muted);
  }
  .detail-toggle:hover { color: var(--text-strong); border-color: var(--text-strong); }
  .detail-row td { padding: 0; }
  .detail-body { padding: 12px 14px; }
  .detail-json {
    margin: 0; font-size: 11.5px; line-height: 1.5;
    white-space: pre-wrap; word-break: break-word;
    max-height: 420px; overflow: auto;
    background: color-mix(in srgb, var(--text-strong) 4%, transparent);
    border-radius: 8px; padding: 12px;
  }
`;
